import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { useCachedFacebookPages } from "@/hooks/useCachedFacebookPages";
import { useCachedInstagramAccounts } from "@/hooks/useCachedInstagramAccounts";
import { useAuth } from "@/contexts/AuthContext";

interface AdProfilesStepProps {
  adAccountId: string;
  fbPageId: string | null;
  fbPageName: string | null;
  igAccountId: string | null;
  igAccountName: string | null;
  onChange: (data: {
    default_fb_page_id: string | null;
    default_fb_page_name: string | null;
    default_ig_account_id: string | null;
    default_ig_account_name: string | null;
  }) => void;
}

export function AdProfilesStep({
  adAccountId,
  fbPageId,
  fbPageName,
  igAccountId,
  igAccountName,
  onChange,
}: AdProfilesStepProps) {
  const { user } = useAuth();
  const { data: fbPages = [], isLoading: fbLoading } = useCachedFacebookPages(user?.id, {
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || d.length === 0) return 3000;
      return false;
    },
  });
  const { data: igAccounts = [], isLoading: igLoading } = useCachedInstagramAccounts(user?.id, {
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || d.length === 0) return 3000;
      return false;
    },
  });

  // Show "syncing" hint for up to 10s after mount if data is empty
  const [mountTime] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (fbPages.length > 0) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [fbPages.length]);
  const isSyncing = fbPages.length === 0 && (fbLoading || now - mountTime < 30000);

  const fbOptions = useMemo(
    () =>
      fbPages.map((p) => ({
        value: p.page_id,
        label: p.page_name,
        subtitle: p.page_id,
      })),
    [fbPages]
  );

  const igOptions = useMemo(
    () =>
      igAccounts.map((a) => ({
        value: a.instagram_id,
        label: `@${a.username}`,
        subtitle: a.instagram_id,
      })),
    [igAccounts]
  );

  const handleFbChange = (value: string) => {
    const page = fbPages.find((p) => p.page_id === value);
    onChange({
      default_fb_page_id: value || null,
      default_fb_page_name: (page?.page_name ?? value) || null,
      default_ig_account_id: igAccountId,
      default_ig_account_name: igAccountName,
    });
  };

  const handleIgChange = (value: string) => {
    const account = igAccounts.find((a) => a.instagram_id === value);
    onChange({
      default_fb_page_id: fbPageId,
      default_fb_page_name: fbPageName,
      default_ig_account_id: value || null,
      default_ig_account_name: account?.username ? `@${account.username}` : (value || null),
    });
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="text-lg">Ad Profiles</CardTitle>
          <CardDescription>
            Choose the default Facebook Page and Instagram account for your ads
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Facebook Page */}
        <div className="space-y-2">
          <Label>Facebook Page</Label>
          {isSyncing ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Syncing your ad account data...
            </div>
          ) : (
            <Combobox
              options={fbOptions}
              value={fbPageId ?? ""}
              onValueChange={handleFbChange}
              placeholder="Select a Facebook Page..."
              searchPlaceholder="Search pages..."
              emptyMessage="No pages found yet. In some situations syncing can take a bit longer — please wait a moment or try refreshing the page."
            />
          )}
        </div>

        {/* Instagram Account */}
        <div className="space-y-2">
          <Label>Instagram Account</Label>
          {isSyncing ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Syncing your ad account data...
            </div>
          ) : (
            <Combobox
              options={igOptions}
              value={igAccountId ?? ""}
              onValueChange={handleIgChange}
              placeholder="Select an Instagram account..."
              searchPlaceholder="Search accounts..."
              emptyMessage="No accounts found yet. In some situations syncing can take a bit longer — please wait a moment or try refreshing the page."
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

